/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, CircularProgress } from '@mui/material';
import React, { useRef, useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Cropper, CropperRef } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import { newNewsFeedUploadFiles } from '../../../../../../../../../../../libs/HelperContent';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

// Interface for CropDialog props
interface CropDialogProps {
	open: boolean;
	imageUrl: string;
	onClose: () => void;
	onCropComplete: (croppedImageUrl: string, croppedImageKey?: string) => void;
	language: any;
	aspectRatio?: number; // Optional: Allow custom aspect ratios
	cropperHeight?: number; // Optional: Allow custom cropper height
	maxFileSize?: number; // Optional: Max file size in MB
	outputFormat?: 'png' | 'jpeg'; // Optional: Output format
	outputQuality?: number; // Optional: Output quality (0-1)
}

const CropDialog: React.FC<CropDialogProps> = ({
	open,
	imageUrl,
	onClose,
	onCropComplete,
	language,
	aspectRatio = 16 / 9, // Default 16:9 aspect ratio
	cropperHeight = 400, // Default height
	maxFileSize = 5, // Default 5MB
	outputFormat = 'png', // Default PNG format
	outputQuality = 0.9 // Default 90% quality
}) =>
{
	const cropperRef = useRef<CropperRef>(null);
	const [ isProcessing, setIsProcessing ] = useState(false);
	const [ isCropperReady, setIsCropperReady ] = useState(false);
	const userData = useSelector((state: any) => state.mysession?.session);


	const handleCrop = async () =>
	{
		if (!cropperRef.current)
		{
			toast.error('Cropper not ready');

			return;
		}

		try
		{
			setIsProcessing(true);
			// Get the cropped canvas
			const canvas = cropperRef.current.getCanvas();

			if (!canvas)
			{
				toast.error('Failed to get cropped image');

				return;
			}

			// Convert canvas to blob
			canvas.toBlob(async (blob) =>
			{
				if (!blob)
				{
					toast.error('Failed to process cropped image');
					setIsProcessing(false);

					return;
				}

				// Check file size
				const fileSizeMB = blob.size / (1024 * 1024);

				if (fileSizeMB > maxFileSize)
				{
					toast.error(`File size (${fileSizeMB.toFixed(2)}MB) exceeds maximum allowed size (${maxFileSize}MB)`);
					setIsProcessing(false);

					return;
				}

				try
				{
					// Create file from blob
					const timestamp = Date.now();
					const fileExtension = outputFormat === 'png' ? 'png' : 'jpg';
					const fileName = `cropped_thumbnail_${timestamp}.${fileExtension}`;
					const mimeType = outputFormat === 'png' ? 'image/png' : 'image/jpeg';
					const file = new File([ blob ], fileName, { type: mimeType });

					// Upload to S3
					const path = `${userData?.companyId}/resource`;
					const uploadResults = await newNewsFeedUploadFiles([ file ], path);

					if (uploadResults && uploadResults.length > 0)
					{
						const uploadedFile = uploadResults[0];

						onCropComplete(uploadedFile.url, uploadedFile.key);
						toast.success('Image cropped and uploaded successfully');
					}
					else
					{
						toast.error('Failed to upload cropped image');
					}
				}
				catch (error)
				{
					console.error('Error uploading cropped image:', error);
					toast.error('Failed to upload cropped image');
				}
				finally
				{
					setIsProcessing(false);
				}
			}, outputFormat === 'png' ? 'image/png' : 'image/jpeg', outputQuality);
		}
		catch (error)
		{
			console.error('Error cropping image:', error);
			toast.error('Failed to crop image');
			setIsProcessing(false);
		}
	};

	const handleCancel = () =>
	{
		setIsProcessing(false);
		setIsCropperReady(false);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={(!isProcessing ) ? handleCancel : undefined}
			maxWidth='md'
			fullWidth
			PaperProps={{
				sx : {
					height    : '80vh',
					maxHeight : '600px'
				}
			}}
		>
			<DialogTitle>
				<Box display='flex' justifyContent='space-between' alignItems='center'>
					<Typography variant='h6'>{language?.crop || 'Crop Image'}</Typography>
					{!isProcessing && (
						<IconButton onClick={handleCancel} size='small'>
							<CloseIcon />
						</IconButton>
					)}
				</Box>
			</DialogTitle>

			<DialogContent sx={{ padding: 0, overflow: 'hidden' }}>
				<Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
					{(isProcessing || !isCropperReady) && (
						<Box
							sx={{
								position        : 'absolute',
								top             : 0,
								left            : 0,
								right           : 0,
								bottom          : 0,
								display         : 'flex',
								alignItems      : 'center',
								justifyContent  : 'center',
								backgroundColor : 'rgba(255, 255, 255, 0.9)',
								zIndex          : 10
							}}
						>
							<Box sx={{ textAlign: 'center' }}>
								<CircularProgress size={40} sx={{ color: '#24a4c4' }} />
								<Typography variant='body2' sx={{ mt: 2, color: '#666' }}>
									{ isProcessing ? 'Processing...' : 'Preparing cropper...'}
								</Typography>
							</Box>
						</Box>
					)}
					<Cropper
						ref={cropperRef}
						src={imageUrl}

						className='cropper'
						stencilProps={{
							aspectRatio // Configurable aspect ratio
						}}
						backgroundClassName='cropper-background'
						style={{ 
							opacity: (isProcessing || !isCropperReady) ? 0 : 1,
							transition: 'opacity 0.2s ease-in-out'
						}}
						onReady={() => {
							// Cropper is fully ready and rendered
							setTimeout(() => setIsCropperReady(true), 50);
						}}
					/>
				</Box>
			</DialogContent>

			<DialogActions sx={{ padding: 2 }}>
				<Button
					onClick={handleCancel}
					disabled={isProcessing }
					variant='outlined'
				>
					{language?.cancel || 'Cancel'}
				</Button>
				<Button
					onClick={handleCrop}
					disabled={isProcessing || !isCropperReady}
					variant='contained'
					sx={{ color: 'var(--whiteColor)' }}
				>
					{isProcessing ? 'Processing...' : ( !isCropperReady) ? 'Loading...' : (language?.crop || 'Crop & Save')}
				</Button>
			</DialogActions>

			{/* Crop Dialog Styles - Applied globally for this component */}
			<style>{`
				.cropper {
					height: ${cropperHeight}px !important;
					background: #f5f5f5 !important;
				}
				
				.cropper-background {
					background: #f0f0f0 !important;
				}

				.react-advanced-cropper {
					background: transparent !important;
				}

				.react-advanced-cropper__image {
					max-height: ${cropperHeight}px !important;
				}

				.react-advanced-cropper__stencil {
					border: 2px solid #24a4c4 !important;
				}

				.react-advanced-cropper__handler {
					background: #24a4c4 !important;
					border: 2px solid white !important;
					border-radius: 4px !important;
					width: 12px !important;
					height: 12px !important;
				}

				.react-advanced-cropper__line {
					border-color: #24a4c4 !important;
				}
			`}</style>
		</Dialog>
	);
};

export default CropDialog;